import { ref, watch, computed, getCurrentInstance, reactive } from "@vue/composition-api";
import fieldsComponentsMap from "./fields/fieldsComponentsMap";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { debounce } from "lodash";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import axios from "@/libs/axios";
import store from "@/store";

export default function useHorizontalForm(emit) {
  const template = ref({});
  const model = ref({});
  const questionsNames = ref([]);
  const headerNames = ref([]);
  const isFormDisabled = ref(false);
  const requiredFields = ref([]);
  const removedRequiredFields = ref([]);
  const errors = ref({});
  const currentFiles = ref([]);
  const isEdit = ref(false);
  const evaluation = ref(null);
  const showFields = ref(true);
  const lastFacilitySelected = ref({});
  const lastRegionOfFacilitySelected = ref("");
  const originalTemplateData = ref([]);

  const isHorizontal = ref(true);
  const user = store.getters["user/getUser"];
  const { t } = useI18nUtils();
  const toast = useToast();

  const translations = {
    "operational_security.evaluation.error_send_form": t(
      "operational_security.evaluation.error_send_form"
    ),
    "operational_security.evaluation.success_send_form": t(
      "operational_security.evaluation.success_send_form"
    ),
    "operational_security.evaluation.answers_successfully": t(
      "operational_security.evaluation.answers_successfully"
    ),
    "operational_security.evaluation.answers_error": t(
      "operational_security.evaluation.answers_error"
    ),
    "operational_security.evaluation.review_successfully": t(
      "operational_security.evaluation.review_successfully"
    ),
    "operational_security.evaluation.review_error": t(
      "operational_security.evaluation.review_error"
    ),
    "operational_security.evaluation.status_successfully": t(
      "operational_security.evaluation.status_successfully"
    ),
    "operational_security.evaluation.status_error": t(
      "operational_security.evaluation.status_error"
    ),
    "operational_security.evaluation.reopen_successfully": t(
      "operational_security.evaluation.reopen_successfully"
    ),
    "operational_security.evaluation.reopen_error": t(
      "operational_security.evaluation.reopen_error"
    ),
    "testing.penetration.required": t("testing.penetration.required"),
    "testing.penetration.perticipantRequired": t(
      "testing.penetration.perticipantRequired"
    ),
  };

  const originalOptions = new Map();
  const CONDITIONAL_FORMS = ["Cierre con impacto", "Pruebas de dispositivos", "Reforzamiento de seguridad"];

  const initForm = (templateData) => {
    template.value = templateData;
    originalTemplateData.value = structuredClone(
      template.value.data ?? []
    );
    questionsNames.value = getUniqueBaseFieldsQuestions([...templateData.data]);
    isHorizontal.value = templateData.is_horizontal || true;
    // questionsNames.value = getUniqueBaseFields([...templateData.data])
    headerNames.value = getHeaderNames([...templateData.data]);
    requiredFields.value = getRequiredBaseNames(questionsNames.value);
    model.value = setModel(getUniqueBaseFields([...templateData.data]));
    // template.value?.data?.forEach((field) => {
    //   originalOptions.set(field.name, [...(field.options?.fieldTypeCfg?.data ?? [])]);
    // });
  };

  const clearForm = () => {
    showFields.value = false;
    setTimeout(() => {
      showFields.value = true;
    }, 200);
    requiredFields.value = getRequiredBaseNames(questionsNames.value);
    model.value = setModel(questionsNames.value);
    emit("fetchData");
  };

  const fieldIdToName = computed(() => {
    const map = {};
    (template.value.data || []).forEach((f) => {
      map[f.item_id] = f.name;
    });
    return map;
  });
  
  const evaluateCondition = (condition, model) => {
    const fieldKey = fieldIdToName.value[condition.field] ?? condition.field;
    const fieldValue = model[fieldKey];
  
    switch (condition.op) {
      case "==": return fieldValue === condition.value;
      case "!=": return fieldValue !== condition.value;
      case ">":  return fieldValue > condition.value;
      case "<":  return fieldValue < condition.value;
      default:   return false;
    }
  };

  const visibleQuestions = computed(() => {
    if (!CONDITIONAL_FORMS.includes(template.value?.name)) {
      return questionsNames.value;
    }

    return questionsNames.value.filter((field) =>
      isFieldVisible(field, model.value)
    );
  });

  const isFieldVisible = (field, model) => {
    if (!field.show_if?.length) return true;

    const results = field.show_if.map((condition) => evaluateCondition(condition, model));

    return field.show_logic === "any" ? results.some(Boolean) : results.every(Boolean);
  };
  const getUniqueBaseFieldsQuestions = (fields) => {

    if(!CONDITIONAL_FORMS.includes(template.value?.name)) {
      const seen = new Set();
  
      return fields.reduce((acc, field) => {
        const baseName = field.name.split("|")[0].trim();
  
        if (!seen.has(baseName)) {
          seen.add(baseName);
          const data = {
            item_id: field.item_id,
            options: field.options,
            show_if: field.show_if,
            type: field.type,
            name: baseName,
            originalName: field.name,
            label: baseName,
          };
          if (field.type !== "folio") acc.push(data);
        }
  
        return acc;
      }, []);
    } else {
      return fields
      .map(field => ({
        item_id: field.item_id,
        options: field.options,
        show_if: field.show_if,
        type: field.type,
        name: field.name,
        originalName: field.name,
        label: field.name.split("|")[0].trim(),
      }));
    }
  };
  const getUniqueBaseFields = (fields) => {
    if(!CONDITIONAL_FORMS.includes(template.value?.name)) {
      const seen = new Set();
  
      return fields.reduce((acc, field) => {
        const baseName = field.name.split("|")[0].trim();
  
        if (!seen.has(baseName)) {
          seen.add(baseName);
          const data = {
            item_id: field.item_id,
            options: field.options,
            show_if: field.show_if,
            type: field.type,
            name: baseName,
            originalName: field.name,
            label: baseName,
          };
          acc.push(data);
        }
  
        return acc;
      }, []);
    } else {
      return fields
        .map(field => ({
          item_id: field.item_id,
          options: field.options,
          show_if: field.show_if,
          type: field.type,
          name: field.name,
          originalName: field.name,
          label: field.name.split("|")[0].trim(),
        }));
    }
  };
  const getHeaderNames = (fields) => {
    if (!isHorizontal.value) {
      return [
        {
          name: template.value?.name ?? "Registro",
          item_id: "register_form",
        },
      ];
    }
    const seen = new Set();

    return fields.reduce((acc, field) => {
      const baseName = field.name.split("|")[0].trim();

      if (!seen.has(baseName)) {
        seen.add(baseName);
        const data = {
          item_id: field.item_id,
          options: field.options,
          show_if: field.show_if,
          type: field.type,
          name: baseName,
          originalName: field.name,
        };
        acc.push(data);
      }

      return acc;
    }, []);
  };

  const resolveComponent = (type) => {
    const mappedType = fieldsComponentsMap[type];

    return mappedType || fieldsComponentsMap["text"];
  };

  const onUpdateFiles = ({ files, label, name: fieldName }) => {
    currentFiles.value = files;
  };

  const updateModel = (field, value) => {
    model.value[field.name] = value;
    if (Object.keys(errors.value).length > 0) onChangeValidate();
    if (!value) return;
    setConditionalOptions(field, value);
    if(CONDITIONAL_FORMS.includes(template.value?.name) && value) {
      cleanHiddenFields(model.value, visibleQuestions.value);
    }
  };

  const cleanHiddenFields = (model, visibleQuestions) => {
    const visibleNames = new Set(
      visibleQuestions.map(question => question.name)
    );
  
    Object.keys(model).forEach(key => {
      if (!visibleNames.has(key)) {
        model[key] = null;
      }
    });
  
    return model;
  };

  const getRequiredBaseNames = (fields) => {
    return [
      ...new Set(
        fields
          .filter((field) => field?.options?.required === true && field.type !== "folio")
          .map((field) => field.originalName.split("|")[0].trim())
      ),
    ];
  };

  const setModel = (fields) => {
    if (!CONDITIONAL_FORMS.includes(template.value?.name)) {
      return fields.reduce((acc, field) => {
        const baseName = field.originalName.split("|")[0].trim();
  
        acc[baseName] =
          field.type === "folio"
            ? field.options.fieldTypeCfg.data[0]?.value
            : null;
  
        return acc;
      }, {});
    }
  
    return fields.reduce((acc, field) => {
      acc[field.name] =
        field.type === "folio"
          ? field.options.fieldTypeCfg.data[0]?.value
          : null;
  
      return acc;
    }, {});
  };

  const validateForm = () => {
    errors.value = {};
    requiredFields.value.forEach((key) => {
      const value = model.value[key];

      if (value === "" || value === null || value === undefined) {
        errors.value[key] = translations["testing.penetration.required"];
      }
    });
    // if (!form.value.rems?.facility?.value) {
    //   errors.value['rems'] = translations["testing.penetration.required"]
    // }

    // validateParticipants(form.value.securityParticipants, 'securityParticipants')
    // validateParticipants(form.value.additionalParticipants, 'additionalParticipants')
    // validateParticipants(form.value.employeesToEvaluated, 'employeesToEvaluated')
    // console.log(errors.value, model.value)
    return Object.keys(errors.value).length === 0;
  };

  const setConditionalOptions = (fieldData, val) => {
    if(fieldData.name === 'Tipo de evento') {
      cleanSelectOptions(fieldData);
    }
    if(fieldData.name === 'Clasificación') {
      cleanSelectOptions(fieldData, 'Subcategoría');
      cleanSelectOptions(fieldData, 'Categoría');
      cleanSelectOptions(fieldData, 'Tipo de Evento.');
    }
    setClassificationOptions(fieldData, val)
    setCategotyOptions(fieldData, val)
    setSubCategoryOptions(fieldData, val)
  };

  const setClassificationOptions = (fieldData, val) => {
    if(fieldData.name !== 'Tipo de evento') return

    const fields = originalTemplateData.value;
  
    const eventTypeQuestion = fields.find(
      ({ name }) => name === 'Tipo de evento'
    );
  
    if (!eventTypeQuestion) return null;
  
    const conditionalField = fields.find(({ show_if }) =>
      show_if?.some(({ field, op, value }) =>
        field === eventTypeQuestion.item_id &&
        op === '==' &&
        value === val
      )
    );
  
    if (!conditionalField) return null;
  
    const baseName = conditionalField.name
      ?.split('|')[0]
      ?.trim();
  
    const index = questionsNames.value.findIndex(
      field => field.name === baseName
    );
  
    if (index === -1) return null;

    const options = structuredClone(conditionalField.options);

    options.fieldTypeCfg.data.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
      })
    );
  
    const updatedField = {
      ...questionsNames.value[index],
      originalName: conditionalField.name,
      originalId: conditionalField.item_id,
      options,
    };
    questionsNames.value.splice(index, 1, updatedField);
  };

  const setCategotyOptions = (fieldData, val) => {
    if(fieldData.name !== 'Clasificación') return
    const conditionalField = template.value?.data.find(({ show_if }) =>
      show_if?.some(({ field, op, value }) =>
        field === fieldData.originalId &&
        op === '==' &&
        value === val
      )
    );
    setValidationOnSubCat(true, val)
    if (!conditionalField) return null;
  
    const baseName = conditionalField.name
      ?.split('|')[0]
      ?.trim();
  
    const index = questionsNames.value.findIndex(
      field => field.name === baseName
    );
  
    if (index === -1) return null;

    const options = structuredClone(conditionalField.options);

    options.fieldTypeCfg.data.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
      })
    );
  
    const updatedField = {
      ...questionsNames.value[index],
      originalName: conditionalField.name,
      originalId: conditionalField.item_id,
      options,
    };
    questionsNames.value.splice(index, 1, updatedField);
  };

  const setSubCategoryOptions = (fieldData, val) => {
    if(fieldData.name !== 'Categoría') return
    const conditionalField = template.value?.data.find(({ show_if }) =>
      show_if?.some(({ field, op, value }) =>
        field === fieldData.originalId &&
        op === '==' &&
        value === val
      )
    );

    setValidationOnSubCat(conditionalField, val)
    if (!conditionalField) return null
  
    const baseName = conditionalField.name
      ?.split('|')[0]
      ?.trim();
  
    const index = questionsNames.value.findIndex(
      field => field.name === baseName
    );
  
    if (index === -1) return null;

    const options = structuredClone(conditionalField.options);

    options.fieldTypeCfg.data.sort((a, b) =>
      a.name.localeCompare(b.name, undefined, {
        sensitivity: 'base',
      })
    );
  
    const updatedField = {
      ...questionsNames.value[index],
      originalName: conditionalField.name,
      originalId: conditionalField.item_id,
      options,
    };
    questionsNames.value.splice(index, 1, updatedField);
    cleanSelectOptions(fieldData, 'Tipo de Evento.');
  };

  const cleanSelectOptions = (fieldData, fieldToClear) => {  
    const fieldsToClear = new Set(fieldToClear ? [fieldToClear] : [
      'Clasificación',
      'Categoría',
      'Subcategoría',
      'Tipo de Evento.'
    ]);
  
    questionsNames.value = questionsNames.value.map(field => {
      if (!fieldsToClear.has(field.name)) {
        return field;
      }
  
      return {
        ...field,
        options: {
          ...field.options,
          fieldTypeCfg: {
            ...field.options?.fieldTypeCfg,
            data: [],
          },
        },
      };
    });
  };

  const setValidationOnSubCat = (hasSubCategory, value) => {

    if (!hasSubCategory && value) {
      questionsNames.value?.forEach((field) => {
        if (field.name === "Subcategoría") {
          field.options.fieldTypeCfg.data = [];
          if (requiredFields.value.includes("Subcategoría")) {
            removedRequiredFields.value.push("Subcategoría");
            requiredFields.value = requiredFields.value.filter(
              (field) => field !== "Subcategoría"
            );
          }
        }
      });
    }

    if (hasSubCategory) {
      if (removedRequiredFields.value.includes("Subcategoría")) {
        requiredFields.value.push("Subcategoría");
        removedRequiredFields.value = removedRequiredFields.value.filter(
          (field) => field !== "Subcategoría"
        );
      }
    }
  };

  const onChangeValidate = debounce(async (query, loading) => {
    validateForm();
  }, 500);

  const submitForm = async () => {
    if(!template.value.allow_draft) {
      if (!validateForm()) return;
    }
    const formData = new FormData();
    const dataToSend = setValuesToSend();
    const fileQuestion = template.value.data.find(
      (item) => item.type === "files_section"
    );
    for (const file of currentFiles.value) {
      if (file.raw) {
        formData.append(`${fileQuestion?.name || "File"}[]`, file.raw);
      } else if (file.url) {
        const blob = await this.convertUrlToBlob(file.url);
        if (blob) formData.append(`${fileQuestion?.name || "File"}[]`, blob, file.name);
      }
    }

    formData.append("data", JSON.stringify(dataToSend));
    formData.append("id_template", template.value.id);

    const config = {
      headers: {
        "Content-Type": "multipart/form-data",
        "X-HTTP-Method-Override": isEdit.value ? "PUT" : "POST",
      },
    };

    try {
      isFormDisabled.value = true;
      const url = isEdit.value
        ? `v1/operational-security-forms/template-answers/${evaluation.value.id}`
        : `v1/operational-security-forms/template-answers`;

      const data = await axios.post(url, formData, config);

      contentToast(
        translations["operational_security.evaluation.answers_successfully"],
        "success",
        "CheckIcon"
      );
      emit("onClose", false);
      clearForm();
    } catch (error) {
      console.log("error", error);
      const errorMessage =
        error?.error?.response?.data?.message ||
        translations["operational_security.evaluation.answers_error"];
      contentToast(errorMessage, "danger", "AlertTriangleIcon");
    } finally {
      isFormDisabled.value = false;
    }
  };

  function contentToast(message, type, icon = "CheckIcon") {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon,
        variant: type,
      },
    });
  }

  const setValuesToSend = () => {
    const result = { ...model.value };
    const context = {
      eventType: normalize(model.value["Tipo de evento"]),
      classification: normalize(model.value["Clasificación"]),
      category: normalize(model.value["Categoría"]),
    };

    Object.entries(model.value).forEach(([key, value]) => {
      if (!value) return;

      const matches = template.value.data.filter((item) =>
        item.name.startsWith(`${key}|`)
      );

      if (!matches.length) return;

      const match = findTemplateMatch(key, matches, context);

      if (!match) return;

      result[match.name] = value;
      delete result[key];
    });

    result[
      "SOEID Analista"
    ] = `${user.user}|${user.name} ${user.first_name} ${user.second_name}`;
    return result;
  };

  const normalize = (str) =>
    str
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();

  const findTemplateMatch = (key, matches, context) => {
    const { eventType, classification, category } = context;
    return matches.find((item) => {
      const condition = item.name.split("|")[1];
      const parts = condition.split("/");

      if (key === "Clasificación") {
        const cond = normalize(parts[0]);
        return cond.includes(eventType) || eventType.includes(cond);
      }

      if (key === "Categoría") {
        const [event, clas] = parts;
        return (
          normalize(event).includes(eventType) && normalize(clas).includes(classification)
        );
      }

      if (key === "Subcategoría") {
        const [, clas, cat] = parts;
        return (
          normalize(clas)?.includes(classification) && normalize(cat)?.includes(category)
        );
      }

      return false;
    });
  };

  function supportOnlyImages(field) {
    return field?.options?.fieldTypeCfg?.data[0]?.onlyImages ?? false;
  }

  function setLastFacilitySelected(facility) {
    lastFacilitySelected.value = facility;
    lastRegionOfFacilitySelected.value = facility?.region;
  }

  function blockNumberWheel(e) {
    if (e?.target?.type !== 'number') return
    e.target.blur()
  }

  function blockSubmit(e) {
    const isNotTextArea = e.key === 'Enter' && e?.target?.tagName !== 'TEXTAREA'
    if (isNotTextArea) {
      e.preventDefault()
      return;
    }

    if (e.key === 'Dead') {
      e.preventDefault()
      return
    }

    const isNumberInput = e?.target?.type === 'number'
    if (!isNumberInput) return
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'
    ]
    const isDigit = /^\d$/.test(e.key)
    if (!isDigit && !allowedKeys.includes(e.key)) {
      e.preventDefault()
    }
  }

  return {
    template,
    model,
    questionsNames,
    isFormDisabled,
    errors,
    headerNames,
    showFields,
    lastFacilitySelected,
    lastRegionOfFacilitySelected,
    isHorizontal,
    visibleQuestions,
    initForm,
    isFieldVisible,
    resolveComponent,
    updateModel,
    submitForm,
    onUpdateFiles,
    supportOnlyImages,
    setLastFacilitySelected,
    blockNumberWheel,
    blockSubmit
  };
}
